import * as pc from 'playcanvas';
import { pointInPath } from './pointInPath';
import { defaultValue } from '../../Components/threeD/common/config';

export const setupSimpleOrbitCamera = (app, camera, target, modelConfig, fullControl) => {
    // Initialize camera position and angles
    const initialPosition = camera.getLocalPosition();
    let orbitAngleX = Math.atan2(
        initialPosition.x - target.getPosition().x, 
        initialPosition.z - target.getPosition().z
    ) * pc.math.RAD_TO_DEG;
    
    let orbitAngleY = Math.atan2(
        initialPosition.y - target.getPosition().y, 
        Math.sqrt((initialPosition.x - target.getPosition().x) ** 2 + (initialPosition.z - target.getPosition().z) ** 2)
    ) * pc.math.RAD_TO_DEG;

    let distance = initialPosition.distance(target.getPosition());

    // Add target variables for smooth transitions
    // let targetOrbitAngleX = orbitAngleX + defaultValue.camera.cameraMotionConfig.common.initialOrbitAngleX;  
    let targetOrbitAngleX = orbitAngleX;
    let targetOrbitAngleY = orbitAngleY;
    let targetDistance = distance;

    // Mouse control variables
    let isDragging = false;
    let lastMousePosition = new pc.Vec2();

    // Configuration
    const config = {
        orbitSensitivity: defaultValue.camera.cameraMotionConfig.common.orbitSensitivity, //0.2
        inertiaFactor: defaultValue.camera.cameraMotionConfig.common.inertiaFactor, //0.2
        zoomInertiaFactor: defaultValue.camera.cameraMotionConfig.common.zoomInertiaFactor, //0.1
        zoomSensitivity: defaultValue.camera.cameraMotionConfig.common.zoomSensitivity, //0.05
        verticalRotationMin: fullControl ? 
            defaultValue.camera.cameraMotionConfig.fullControl.verticalRotationMin : 
            defaultValue.camera.cameraMotionConfig.common.verticalRotationMin, //2
        verticalRotationMax: fullControl ? 
            defaultValue.camera.cameraMotionConfig.fullControl.verticalRotationMax : 
            defaultValue.camera.cameraMotionConfig.common.verticalRotationMax, //7
        minDistance: fullControl ? 
            defaultValue.camera.cameraMotionConfig.fullControl.minDistance : 
            modelConfig.torus_config.torus_inner_radius,
        maxDistance: fullControl ?
            defaultValue.camera.cameraMotionConfig.fullControl.maxDistance :
            Number(modelConfig.torus_config.torus_inner_radius) + (Number(modelConfig.torus_config.torus_ring_radius) * 2),
    };


    // Mouse event handlers
    app.mouse.on(pc.EVENT_MOUSEDOWN, (event) => {
        if (event.button === pc.MOUSEBUTTON_LEFT) {
            isDragging = true;
            lastMousePosition.set(event.x, event.y);
        }
    });

    app.mouse.on(pc.EVENT_MOUSEUP, () => {
        isDragging = false;
    });

    app.mouse.on(pc.EVENT_MOUSEMOVE, (event) => {
        if (isDragging) {
            const dx = event.x - lastMousePosition.x;
            const dy = event.y - lastMousePosition.y;

            targetOrbitAngleX -= dx * config.orbitSensitivity;
            targetOrbitAngleY += dy * config.orbitSensitivity;
            targetOrbitAngleY = pc.math.clamp(
                targetOrbitAngleY, 
                config.verticalRotationMin, 
                config.verticalRotationMax
            );

            lastMousePosition.set(event.x, event.y);
        }
    });

    // Wheel event handler for zooming
    const handleWheel = (event) => {
        event.preventDefault();
        event.stopPropagation();
        targetDistance = Math.max(
            config.minDistance, 
            Math.min(config.maxDistance, 
                targetDistance + event.deltaY * config.zoomSensitivity
            )
        );
    };

    const canvasElement = app.graphicsDevice.canvas;
    canvasElement.addEventListener('wheel', handleWheel, { passive: false });

    // Touch variables
    let isTouching = false;
    let lastTouchPosition = new pc.Vec2();
    let initialDistanceBetweenTouches = null;

    // Touch event handlers
    const handleTouchStart = (event) => {
        if (event.touches.length === 1) {
            isTouching = true;
            lastTouchPosition.set(event.touches[0].x, event.touches[0].y);
        }
        else if (event.touches.length === 2) {
            initialDistanceBetweenTouches = Math.hypot(
                event.touches[0].x - event.touches[1].x,
                event.touches[0].y - event.touches[1].y
            );
        }
    };

    const handleTouchMove = (event) => {
        if (isTouching && event.touches.length === 1) {
            const dx = event.touches[0].x - lastTouchPosition.x;
            const dy = event.touches[0].y - lastTouchPosition.y;

            targetOrbitAngleX -= dx * config.orbitSensitivity;
            targetOrbitAngleY += dy * config.orbitSensitivity;
            targetOrbitAngleY = pc.math.clamp(
                targetOrbitAngleY, 
                config.verticalRotationMin, 
                config.verticalRotationMax
            );

            lastTouchPosition.set(event.touches[0].x, event.touches[0].y);
        } else if (event.touches.length === 2) {
            const currentDistance = Math.hypot(
                event.touches[0].x - event.touches[1].x,
                event.touches[0].y - event.touches[1].y
            );
            const distanceDelta = currentDistance - initialDistanceBetweenTouches;

            targetDistance = Math.max(
                config.minDistance, 
                Math.min(config.maxDistance, 
                    targetDistance - distanceDelta * config.zoomSensitivity
                )
            );
            initialDistanceBetweenTouches = currentDistance;
        }
    };

    const handleTouchEnd = (event) => {
        if (event.touches.length === 0) {
            isTouching = false;
        }
    };

    // Attach touch event listeners
    const touch = app.touch;
    if (touch) {
        touch.on(pc.EVENT_TOUCHSTART, handleTouchStart);
        touch.on(pc.EVENT_TOUCHMOVE, handleTouchMove);
        touch.on(pc.EVENT_TOUCHEND, handleTouchEnd);
        touch.on(pc.EVENT_TOUCHCANCEL, handleTouchEnd);
    }

    // Update camera position each frame
    app.on('update', () => {
        // Apply inertia to movements
        orbitAngleX = pc.math.lerp(orbitAngleX, targetOrbitAngleX, config.inertiaFactor);
        orbitAngleY = pc.math.lerp(orbitAngleY, targetOrbitAngleY, config.inertiaFactor);
        distance = pc.math.lerp(distance, targetDistance, config.zoomInertiaFactor);

        const targetPos = target.getPosition();
        const x = targetPos.x + distance * Math.sin(pc.math.DEG_TO_RAD * orbitAngleX) * Math.cos(pc.math.DEG_TO_RAD * orbitAngleY);
        const y = targetPos.y + distance * Math.sin(pc.math.DEG_TO_RAD * orbitAngleY);
        const z = targetPos.z + distance * Math.cos(pc.math.DEG_TO_RAD * orbitAngleX) * Math.cos(pc.math.DEG_TO_RAD * orbitAngleY);

        camera.setPosition(x, y, z);
        // const targetPos = target.getPosition();
camera.lookAt(targetPos.x, targetPos.y, targetPos.z);
    });

    // Cleanup event listeners
    app.on('destroy', () => {
        canvasElement.removeEventListener('wheel', handleWheel);
        if (touch) {
            touch.off(pc.EVENT_TOUCHSTART, handleTouchStart);
            touch.off(pc.EVENT_TOUCHMOVE, handleTouchMove);
            touch.off(pc.EVENT_TOUCHEND, handleTouchEnd);
            touch.off(pc.EVENT_TOUCHCANCEL, handleTouchEnd);
        }
    });

    // Return zoom control functions
    // return {
    //     zoomIn: () => {
    //         targetDistance = Math.max(config.minDistance, targetDistance - 1);
    //     },
    //     zoomOut: () => {
    //         targetDistance = Math.min(config.maxDistance, targetDistance + 1);
    //     }
    // };
};